import { Vector2, Vector3 } from "three";
import { pageCoordToNormalizedDeviceCoord } from "../../utils/camera-utils";
import { InputHandler } from "../../utils/input-handler/input-handler";
import {
  IInputEventHandler,
  InputEventType,
  InputMultiPointerEvent,
  InputPointerType,
  IPinchArgs,
  ISwipeArgs,
  SupportedKeyboardKey,
} from "../../utils/input-handler/interface";
import { debounce } from "../../utils/utils";
import { Viewer } from "../../viewer";
import { CameraManager } from "../CameraManager";

export type TPointPickerCallbackResponse = { point: Vector3 };
export interface IPointPickerCallback {
  onClick: (response: TPointPickerCallbackResponse) => void;
}

export class ControlManager implements IInputEventHandler {
  private _viewer: Viewer;
  private _cameraManager: CameraManager;
  private _inputHandler: InputHandler;

  private _ctrl: boolean;

  private _isAuxiliaryClicked: boolean;

  constructor(
    viewer: Viewer,
    cameraManager: CameraManager,
    inputHandler: InputHandler
  ) {
    this._viewer = viewer;
    this._cameraManager = cameraManager;
    this._inputHandler = inputHandler;
    this._isAuxiliaryClicked = false;
  }

  public handlePointerEvent(
    pointer: { pointerType: InputPointerType; eventType: InputEventType },
    event: PointerEvent
  ) {
    let { clientX, clientY } = event;
    switch (pointer.eventType) {
      case InputEventType.START_DRAG:
        if (!this.operationEvent) {
          const startEv = new CustomEvent("startController");
          document.dispatchEvent(startEv);
        }

        this._cameraManager.cameraStart({ pageX: clientX, pageY: clientY });
        this._ctrl = this._inputHandler.supportedKeyMap.get(
          SupportedKeyboardKey.CTRL
        );
        if (event.pointerType === "touch") {
          this._cameraManager.rotateStart({ pageX: clientX, pageY: clientY });
        } else {
          if (
            (pointer.pointerType === InputPointerType.PRIMARY && this._ctrl) ||
            pointer.pointerType === InputPointerType.AUXILARY
          ) {
            this._isAuxiliaryClicked = true;
            this._cameraManager.rotateStart({ pageX: clientX, pageY: clientY });
          }
        }
        break;
      case InputEventType.DRAGGED:
        if (event.pointerType === "touch") {
          this._cameraManager.cameraRotate({ pageX: clientX, pageY: clientY });
        } else {
          if (pointer.pointerType === InputPointerType.PRIMARY) {
            this._ctrl
              ? this._cameraManager.cameraRotate({
                  pageX: clientX,
                  pageY: clientY,
                })
              : this._cameraManager.cameraPan({
                  pageX: clientX,
                  pageY: clientY,
                });
          } else if (pointer.pointerType === InputPointerType.AUXILARY) {
            this._cameraManager.cameraRotate({
              pageX: clientX,
              pageY: clientY,
            });
          } else if (pointer.pointerType === InputPointerType.SECONDARY) {
            const control = this._cameraManager.getControl();
            const previous = control.alternativeWheel;
            control.alternativeWheel = true;
            this._cameraManager.cameraZoom({ pageX: clientX, pageY: clientY });
            control.alternativeWheel = previous;
          }
        }
        break;

      case InputEventType.END_DRAG:
        // const endEv = new CustomEvent("endController");
        // document.dispatchEvent(endEv);

        const endDebounce = debounce(() => {
          const ev = new CustomEvent("endController");
          document.dispatchEvent(ev);
          this.operationEvent = false;
          this._isAuxiliaryClicked = false;
        });

        endDebounce(1000);

        this._cameraManager.cameraEnd();
        if (event.pointerType === "touch") {
          this._cameraManager.rotateEnd();
        } else {
          if (pointer.pointerType === InputPointerType.PRIMARY && this._ctrl) {
            this._cameraManager.rotateEnd();
          } else if (pointer.pointerType === InputPointerType.AUXILARY) {
            this._cameraManager.rotateEnd();
          }
        }
        break;
      default:
        break;
    }
    // }
  }

  public handleMultiplePointerEvent(
    eventType: InputMultiPointerEvent,
    args: ISwipeArgs | IPinchArgs
  ): void {
    switch (eventType) {
      case InputMultiPointerEvent.SWIPE:
        const swipeArg = args as ISwipeArgs;
        this._cameraManager.cameraStart({
          pageX: swipeArg.swipeFrom.x,
          pageY: swipeArg.swipeFrom.y,
        });
        this._cameraManager.cameraPan({
          pageX: swipeArg.swipeTo.x,
          pageY: swipeArg.swipeTo.y,
        });
        this._cameraManager.cameraEnd();
        break;
      case InputMultiPointerEvent.PINCH:
        const pinchArg = args as IPinchArgs;
        const zoomval = pinchArg.pinchOffset >= 0 ? 1 : -1;
        this._cameraManager.pinchZoom(zoomval, this._viewer.getBoundingBox());
        break;
      default:
        break;
    }
  }

  private operationEvent = false;
  public handleWheelEvent(ev: WheelEvent): void {
    if (!this.operationEvent) {
      const ev = new CustomEvent("startController");
      document.dispatchEvent(ev);
      this.operationEvent = true;
    }

    const endDebounce = debounce(() => {
      const ev = new CustomEvent("endController");
      document.dispatchEvent(ev);
      this.operationEvent = false;
    });

    endDebounce(1000);

    if (!this._isAuxiliaryClicked) {
      this._cameraScroll({ ev: ev });
    }
  }

  // public handleWheelEvent(ev: WheelEvent): void {
  //   this._cameraScroll({ ev: ev });
  // }

  protected _cameraPointerZoom(payLoad: { ev: WheelEvent }): void {
    payLoad.ev.preventDefault();
    const coord = new Vector2();
    pageCoordToNormalizedDeviceCoord(
      payLoad.ev,
      this._viewer.getBoundingRect(),
      coord
    );
    const delta = Math.max(
      -1,
      Math.min(1, payLoad.ev.deltaY || -payLoad.ev.detail)
    ) as 1 | -1;
    this._cameraManager.wheelZoom(delta, this._viewer.getBoundingBox());
    this._viewer.updateRenderer(1);
  }

  public _cameraScroll(payLoad: { ev: WheelEvent }): void {
    payLoad.ev.preventDefault();
    const coord = new Vector2();
    pageCoordToNormalizedDeviceCoord(
      payLoad.ev,
      this._viewer.getBoundingRect(),
      coord
    );
    const delta = Math.max(
      -1,
      Math.min(1, payLoad.ev.deltaY || -payLoad.ev.detail)
    ) as 1 | -1;
    this._cameraManager.wheelZoom(delta, this._viewer.getBoundingBox());
    this._viewer.updateRenderer(1);
  }
}
